import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHouse,
  faBox,
  faGear,
  faCarSide,
  faAddressCard,
  faFileInvoiceDollar,
  faCalendarDays,
  faFileLines,
  faScroll,
  faChartLine,
  faHourglassHalf,
  faRightFromBracket,
  faBars,
  faXmark,
  faChevronDown,
  faChevronUp,
  faDna,
  faTools,
  faPuzzlePiece,
  faChartBar,
  faTag,
  faFolderOpen,
  faSliders,
  faUserTie,
  faBullseye,
  faCloud,
  faGift,
  faGlobe,
  faStore,
  faImage,
  faCar,
  faToolbox,
} from '@fortawesome/free-solid-svg-icons';
import { fetchCurrentUser, MeResponse } from '../api/auth';
import { clearAuthToken } from '../utils/auth';
import FallbackImage from '../components/FallbackImage';

interface AppShellProps extends PropsWithChildren {
  title?: string;
}

const NAV_ITEMS = [
  { icon: faHouse, label: 'Dashboard', path: '/dashboard' },
  {
    icon: faBox,
    label: 'Inventory',
    path: '/inventory',
    subMenu: [
      { key: 'masters', label: 'Master Products', icon: faBox, path: '/inventory/masters' },
      { key: 'variants', label: 'Product Variants', icon: faDna, path: '/inventory/variants' },
      { key: 'services', label: 'Services', icon: faTools, path: '/inventory/services' },
      { key: 'service-variants', label: 'Service Variants', icon: faPuzzlePiece, path: '/inventory/service-variants' },
      { key: 'stock', label: 'Current Stock', icon: faChartBar, path: '/inventory/stock' },
      { key: 'brands', label: 'Brands', icon: faTag, path: '/inventory/brands' },
      { key: 'categories', label: 'Categories', icon: faFolderOpen, path: '/inventory/categories' },
      { key: 'attributes', label: 'Attributes', icon: faSliders, path: '/inventory/attributes' },
      { key: 'templates', label: 'Templates', icon: faFileLines, path: '/inventory/templates' },
    ],
  },
  {
    icon: faGear,
    label: 'Account Setup',
    path: '/account-setup',
    subMenu: [
      { key: 'employees', label: 'Employees', icon: faUserTie, path: '/account-setup/employees' },
      { key: 'promotions', label: 'Promotions', icon: faBullseye, path: '/account-setup/promotions' },
      { key: 'dream-prices', label: 'Dream Prices', icon: faCloud, path: '/account-setup/dream-prices' },
      { key: 'complimentary-item-reasons', label: 'Complimentary Reasons', icon: faGift, path: '/account-setup/complimentary-item-reasons' },
      { key: 'countries', label: 'Countries', icon: faGlobe, path: '/account-setup/countries' },
      { key: 'locations', label: 'Locations', icon: faStore, path: '/account-setup/locations' },
      { key: 'banners', label: 'Banners', icon: faImage, path: '/account-setup/banners' },
    ],
  },
  {
    icon: faCarSide,
    label: 'Vehicle Management',
    path: '/vehicle-management',
    subMenu: [
      { key: 'brands', label: 'Brands', icon: faCar, path: '/vehicle-management/brands' },
      { key: 'models', label: 'Models', icon: faCarSide, path: '/vehicle-management/models' },
      { key: 'checklists', label: 'Checklists', icon: faFileLines, path: '/vehicle-management/checklists' },
      { key: 'bays', label: 'Bays', icon: faToolbox, path: '/vehicle-management/bays' },
    ],
  },
  { icon: faAddressCard, label: 'CRM', path: '/crm' },
  { icon: faFileInvoiceDollar, label: 'Order Management', path: '/order-management' },
  { icon: faTools, label: 'Scheduler', path: '/scheduler' },
  { icon: faCalendarDays, label: 'Appointment', path: '/appointments' },
  // { icon: '📊', label: 'Reports', path: '/reports' },
  { icon: faFileLines, label: 'Sync Logs', path: '/logs' },
  { icon: faScroll, label: 'Activity', path: '/activity' },
];

const AppShell: FC<AppShellProps> = ({ children, title }) => {
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const activePath = useMemo(() => {
    const trimmed = location.pathname.replace(/\/$/, '');
    return trimmed.length ? trimmed : '/';
  }, [location.pathname]);

  const role = (me?.role ?? me?.user?.typeKey ?? '') as string;

  const allowedNavItems = useMemo(() => {
    const upper = (role || '').toUpperCase();
    if (upper === 'STORE_MANAGER') {
      return [
        { icon: faHourglassHalf, label: 'Pending Orders', path: '/order-management/pending' },
        { icon: faFileInvoiceDollar, label: 'All Orders', path: '/order-management' },
        { icon: faTools, label: 'Scheduler', path: '/scheduler' },
      ];
    }
    if (upper === 'ADVISOR') {
      return [
        { icon: faHouse, label: 'Dashboard', path: '/dashboard' },
        { icon: faAddressCard, label: 'CRM', path: '/crm' },
        { icon: faFileInvoiceDollar, label: 'Order Management', path: '/order-management' },
        { icon: faTools, label: 'Scheduler', path: '/scheduler' },
        { icon: faCalendarDays, label: 'Appointment', path: '/appointments' },
      ];

    }
    // Default: full menu, but add Sales Pipeline (as 2nd item) only for ADMIN/DIRECTOR
    const base = [...NAV_ITEMS];
    if (['ADMIN', 'DIRECTOR'].includes(upper)) {
      base.splice(1, 0, { icon: faChartLine, label: 'Sales Pipeline', path: '/sales-pipelines' });
    }
    return base;
  }, [role]);

  const activeNavItem = useMemo(() => {
    if (activePath === '/') {
      return allowedNavItems[0];
    }

    // Special-case: manager approval route should highlight Pending Orders
    const upper = (role || '').toUpperCase();
    if (upper === 'STORE_MANAGER' && activePath.startsWith('/order-management/manager-approval')) {
      const pending = allowedNavItems.find((i) => i.path === '/order-management/pending');
      if (pending) return pending;
    }

    // Prefer the longest matching path among allowed items
    const match = allowedNavItems
      .filter((item) =>
        item.path === '/'
          ? activePath === '/'
          : activePath === item.path || activePath.startsWith(`${item.path}/`),
      )
      .sort((a, b) => (b.path?.length ?? 0) - (a.path?.length ?? 0))[0];

    return match ?? allowedNavItems[0];
  }, [activePath, allowedNavItems, role]);

  const appName = process.env.REACT_APP_NAME || 'Workshop';
  const defaultLogoSrc = process.env.REACT_APP_LOGO || '/logo192.png';
  const faviconHref = process.env.REACT_APP_FAVICON || '/favicon.ico';
  const user = me?.user ?? null;
  const companyConfig = me?.companyConfig ?? null;

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let isMounted = true;

    fetchCurrentUser()
      .then((profile) => {
        if (isMounted) {
          setMe(profile);
          setLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setError('Unable to load your profile. Please login again.');
          clearAuthToken();
          navigate('/login', { replace: true });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const pageTitle = title ?? activeNavItem.label;

  const companyName = useMemo(() => {
    if (companyConfig?.companyName) {
      return companyConfig.companyName;
    }
    return appName;
  }, [appName, companyConfig?.companyName]);

  const companyIdentifier = useMemo(() => {
    if (companyConfig?.companyCode) {
      return companyConfig.companyCode.toUpperCase();
    }
    if (companyConfig?.companyEmail) {
      return companyConfig.companyEmail;
    }
    return null;
  }, [companyConfig?.companyCode, companyConfig?.companyEmail]);

  const companyLogo = useMemo(() => {
    return (
      companyConfig?.lightLogo ||
      companyConfig?.darkLogo ||
      defaultLogoSrc
    );
  }, [companyConfig?.darkLogo, companyConfig?.lightLogo, defaultLogoSrc]);

  useEffect(() => {
    document.title = `${companyName} – ${pageTitle}`;

    const favicon = document.querySelector("link[rel='icon']") as
      | HTMLLinkElement
      | null;

    if (favicon) {
      favicon.href = faviconHref;
    }
  }, [companyName, faviconHref, pageTitle]);

  // Restrict STORE_MANAGER to allowed pages and default to Pending Orders
  useEffect(() => {
    const upper = (role || '').toUpperCase();
    if (upper !== 'STORE_MANAGER') return;
    const allowed = ['/order-management', '/order-management/pending', '/scheduler'];
    const isAllowed = allowed.some((p) => activePath === p || activePath.startsWith(p + '/'));
    if (!isAllowed) {
      navigate('/order-management/pending', { replace: true });
    }
  }, [activePath, navigate, role]);

  useEffect(() => {
    if (!isProfileOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-profile-menu]')) {
        setIsProfileOpen(false);
      }
    };

    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, [isProfileOpen]);

  const displayName = useMemo(() => {
    if (!user) {
      return '';
    }

    if (user.firstName || user.lastName) {
      return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    }

    return user.username;
  }, [user]);

  const userInitials = useMemo(() => {
    if (displayName) {
      const parts = displayName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '');
      const initials = parts.join('');
      if (initials) {
        return initials;
      }
    }

    if (user?.username) {
      return user.username.slice(0, 2).toUpperCase();
    }

    return 'U';
  }, [displayName, user?.username]);

  const roleLabel = useMemo(() => {
    const raw = (me?.role ?? me?.user?.typeKey ?? '') as string;
    if (!raw) return null;
    return raw.toString().replace(/_/g, ' ');
  }, [me?.role, me?.user?.typeKey]);

  const currentDate = useMemo(
    () =>
      now.toLocaleDateString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [now],
  );

  const currentTime = useMemo(
    () =>
      now.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
    [now],
  );

  const headerGradientStyle = useMemo(
    () => ({
      background:
        'linear-gradient(115deg, #2b318d 0%, #363fa3 50%, #222b78 100%)',
    }),
    [],
  );

  const handleLogout = () => {
    clearAuthToken();
    navigate('/login', { replace: true });
  };

  if (loading) {
    return <div className="p-6 text-slate-600">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-6 text-danger">
        {error}
        <div className="mt-4">
          <button
            type="button"
            className="rounded bg-primary px-4 py-2 text-white"
            onClick={() => navigate('/login', { replace: true })}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#e8ecf7] text-slate-800">
      <div className="px-4 pt-10 sm:px-6 lg:px-10">
        <div className="relative mx-auto w-full max-w-[96rem]">
          <div className="mx-5 pointer-events-none absolute inset-0 -translate-y-2 rounded-[12px] bg-[#1d2663]/35 shadow-[0_24px_40px_-28px_rgba(20,30,80,0.6)]" />
          <header
            style={headerGradientStyle}
            className="relative flex items-center justify-between gap-2 rounded-[10px] border border-white/20 bg-[#2f3c9f]/95 px-4 py-2 text-white shadow-[-8px_14px_32px_-20px_rgba(18,28,82,0.75)] ring-1 ring-white/15 backdrop-blur-[2px] lg:px-7 lg:gap-4"
          >
            <div className="flex items-center gap-2 lg:gap-3">
              <button
                type="button"
                className="flex items-center justify-center p-2 text-white lg:hidden"
                onClick={() => setIsMobileMenuOpen(true)}
              >
                <FontAwesomeIcon icon={faBars} className="h-6 w-6" />
              </button>
              <div className="max-w-[120px] sm:max-w-none">
                <h1 className="truncate text-lg font-semibold lg:text-xl">Workshop Management System</h1>
                {companyName ? (
                  <p className="truncate text-[10px] text-white/80 lg:text-xs">{companyName}</p>
                ) : null}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 lg:gap-4">
              <div className="text-right">
                <p className="max-w-[80px] truncate text-[11px] font-medium sm:max-w-none lg:text-sm">{displayName}</p>
                <p className="hidden text-[10px] text-white/70 sm:block lg:text-xs">
                  {currentDate} {currentTime}
                </p>
              </div>

              <div className="relative" data-profile-menu>
                <button
                  type="button"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-primary shadow-lg ring-1 ring-slate-200 transition duration-150 hover:scale-[1.03] lg:h-12 lg:w-12"
                  onClick={() => setIsProfileOpen((prev) => !prev)}
                  title="Profile"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    className="h-6 w-6 fill-current text-[#1d47ba] lg:h-7 lg:w-7"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12 3a4 4 0 100 8 4 4 0 000-8zm-6.75 17.25a.75.75 0 01-.75-.75 7.5 7.5 0 0115 0 .75.75 0 01-.75.75h-13.5z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {isProfileOpen && (
                  <div className="absolute right-0 mt-3 w-64 origin-top-right rounded-2xl bg-white p-4 text-slate-700 shadow-xl">
                    <div className="flex items-center gap-4 rounded-xl bg-slate-100/70 p-3 text-slate-800">
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-lg font-semibold text-[#1d47ba] shadow-md ring-1 ring-slate-200">
                        <span>{userInitials}</span>
                      </div>
                      <div className="leading-tight">
                        <div className="font-medium">{displayName}</div>
                        {user?.erpId && (
                          <div className="text-xs text-slate-500">{user.erpId}</div>
                        )}
                      </div>
                    </div>
                    {roleLabel ? (
                      <div className="mt-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
                        <span className="font-semibold text-slate-700">Role:</span> <span className="uppercase tracking-wide">{roleLabel}</span>
                      </div>
                    ) : null}

                    <div className="mt-4 rounded-xl bg-slate-100 p-3">
                      <button
                        type="button"
                        className="flex w-full items-center justify-center gap-2 text-sm font-medium text-primary"
                        onClick={handleLogout}
                      >
                        <span>
                          <FontAwesomeIcon icon={faRightFromBracket} />
                        </span> Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>
        </div>

        <nav className="mx-auto mt-6 hidden w-full max-w-[96rem] rounded-[28px] bg-white/90 px-3 py-3 text-sm font-medium text-slate-600 shadow-[0_14px_30px_-22px_rgba(37,40,85,0.55)] lg:block">
          <div className="flex flex-wrap items-center gap-2.5">
            {allowedNavItems.map((item) => {
              const isActive = item.path === activeNavItem.path;

              return (
                <button
                  key={item.label}
                  type="button"
                  className={`flex items-center gap-2 rounded-full px-2.5 py-2 transition-colors ${isActive
                    ? 'bg-primary/10 text-primary shadow-sm'
                    : 'text-slate-500 hover:text-primary'
                    }`}
                  onClick={() => {
                    if (item.path && activePath !== item.path) {
                      navigate(item.path);
                    }
                  }}
                >
                  <span className={isActive ? 'text-primary' : 'text-primary/70'}>
                    <FontAwesomeIcon icon={item.icon} className="w-4 h-4" />
                  </span>
                  {item.label}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Mobile Navigation Drawer */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-[100] lg:hidden">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            {/* Drawer Content */}
            <div className="absolute inset-y-0 left-0 flex w-72 flex-col bg-white shadow-2xl">
              <div
                style={headerGradientStyle}
                className="flex items-center justify-between p-6 text-white"
              >
                <div>
                  <h2 className="text-xl font-semibold">Workshop Management System</h2>
                  {companyName && (
                    <p className="text-xs text-white/70">{companyName}</p>
                  )}
                </div>
                <button
                  type="button"
                  className="p-1 text-white/80 hover:text-white"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <FontAwesomeIcon icon={faXmark} className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-6">
                <nav className="flex flex-col gap-2">
                  {allowedNavItems.map((item) => {
                    const isActive = item.path === activeNavItem.path;
                    const hasSubMenu = item.subMenu && item.subMenu.length > 0;
                    const isExpanded = expandedMenus[item.label];

                    return (
                      <div key={item.label}>
                        <button
                          type="button"
                          className={`flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm font-medium transition ${isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-primary'
                            }`}
                          onClick={() => {
                            if (hasSubMenu) {
                              setExpandedMenus((prev) => ({
                                ...prev,
                                [item.label]: !prev[item.label],
                              }));
                            } else {
                              if (item.path) {
                                navigate(item.path);
                                setIsMobileMenuOpen(false);
                              }
                            }
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <FontAwesomeIcon
                              icon={item.icon}
                              className={`h-4 w-4 ${isActive ? 'text-primary' : 'text-slate-400'
                                }`}
                            />
                            <span>{item.label}</span>
                          </div>
                          {hasSubMenu && (
                            <FontAwesomeIcon
                              icon={isExpanded ? faChevronUp : faChevronDown}
                              className="h-3 w-3 text-slate-400"
                            />
                          )}
                        </button>

                        {hasSubMenu && isExpanded && (
                          <div className="mt-1 flex flex-col gap-1 pl-10">
                            {item.subMenu.map((sub) => (
                              <button
                                key={sub.label}
                                type="button"
                                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-xs font-medium transition ${activePath === sub.path ||
                                  activePath.startsWith(sub.path + '/')
                                  ? 'text-primary'
                                  : 'text-slate-500 hover:text-primary'
                                  }`}
                                onClick={() => {
                                  navigate(sub.path);
                                  setIsMobileMenuOpen(false);
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={sub.icon}
                                  className="h-3.5 w-3.5 opacity-60"
                                />
                                <span>{sub.label}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </nav>
              </div>

              <div className="border-t border-slate-100 p-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-[#1d47ba] ring-1 ring-slate-200">
                    <span className="text-sm font-semibold">{userInitials}</span>
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <p className="truncate text-sm font-medium text-slate-800">
                      {displayName}
                    </p>
                    {roleLabel && (
                      <p className="truncate text-xs text-slate-500 uppercase">
                        {roleLabel}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-medium text-primary hover:bg-slate-200"
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                >
                  <FontAwesomeIcon icon={faRightFromBracket} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <main className="mx-auto mt-6 w-full max-w-[96rem] px-4 pb-12 text-left text-slate-600 sm:px-6 lg:px-8">
        {children ?? <Outlet />}
      </main>
    </div>
  );
};

export default AppShell;
